import {
  FETCH_RECIPIENT_COUNTRIES_REQUEST,
  FETCH_RECIPIENT_COUNTRIES_SUCCESS,
  FETCH_RECIPIENT_FAILURE,
  FETCH_RECIPIENT_REQUEST,
  FETCH_RECIPIENT_SUCCESS,
  SET_DEFAULT_FIELD_VALUES,
  SET_FIELDS_VALUES,
} from 'constants/index';
import { schemas } from 'constants/schemas';
import get from 'lodash/get';
import type { Recipient } from 'models';
import { normalize } from 'normalizr';
import type { RootState } from 'reducers/types';
import type { Dispatch } from 'redux';
import {
  getCurrentLocale,
  getFieldByEntity,
  getFieldValue,
  isFetching,
} from 'selectors';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import type { RecipientsService } from '../../../services/apiV3/recipients/recipients';

type DispatchAction = Dispatch<{ type: string; payload?: unknown }>;

export const fetchRecipient =
  (id: string) =>
  (dispatch: DispatchAction, getState: () => RootState, { apiV3 }: any) => {
    const state = getState();

    if (isFetching(state, 'recipient')) {
      return Promise.resolve();
    }

    dispatch({
      type: FETCH_RECIPIENT_REQUEST,
    });

    const locale = getCurrentLocale(state);

    return (apiV3.recipients as RecipientsService)
      .fetch({ id, locale })
      .then(response => handleRecipientResponse(response, dispatch, state))
      .catch(error => {
        handleRecipientError(error, dispatch);
        throw error;
      });
  };

export const fetchRecipientBySubdomain =
  (subdomain: string) =>
  (dispatch: DispatchAction, getState: () => RootState, { apiV3 }: any) => {
    const state = getState();

    const isFetchingRecipient = isFetching(state, 'recipient');
    if (isFetchingRecipient) {
      return Promise.resolve();
    }

    dispatch({
      type: FETCH_RECIPIENT_REQUEST,
    });

    const locale = getCurrentLocale(state);

    return (apiV3.recipients as RecipientsService)
      .fetchBySubdomain({ subdomain, locale })
      .then(response => {
        handleRecipientResponse(response, dispatch, state);
      })
      .catch(() => {
        return (apiV3.recipients as RecipientsService)
          .fetch({ id: subdomain, locale })
          .then(response => handleRecipientResponse(response, dispatch, state))
          .catch(error => handleRecipientError(error, dispatch));
      });
  };

export const fetchRecipientByCode =
  (id: string) =>
  (dispatch: DispatchAction, getState: () => RootState, { apiV3 }: any) => {
    const state = getState();

    const isFetchingRecipient = isFetching(state, 'recipient');
    if (isFetchingRecipient) {
      return Promise.resolve();
    }

    dispatch({
      type: FETCH_RECIPIENT_REQUEST,
    });

    const locale = getCurrentLocale(state);

    return (apiV3.recipients as RecipientsService)
      .fetch({ id, locale })
      .then(response => handleRecipientResponse(response, dispatch, state))
      .catch(error => handleRecipientError(error, dispatch));
  };

const handleRecipientResponse = (
  response: Recipient,
  dispatch: DispatchAction,
  state: any,
) => {
  const payload = normalize(response, schemas.recipient);

  dispatch({
    type: FETCH_RECIPIENT_SUCCESS,
    payload,
  });

  const payloadWithoutPrefilledFields = removePrefilledFields(state, payload);

  dispatch({
    type: SET_DEFAULT_FIELD_VALUES,
    payload: payloadWithoutPrefilledFields,
  });
};

const handleRecipientError = (error: any, dispatch: DispatchAction) => {
  errorNotifier.notify(error);
  dispatch({ type: FETCH_RECIPIENT_FAILURE });
};

const removePrefilledFields = (state: any, payload: any) => {
  const NO_VALUE = '';
  const newPayload = { ...payload };

  newPayload.entities = { ...payload.entities };

  const {
    entities: { fields = {} },
  } = newPayload;

  newPayload.entities.fields = Object.keys(fields)
    .filter(field => getFieldValue(state, field) === NO_VALUE)
    .reduce((acc, key) => {
      acc[key] = fields[key];
      return acc;
    }, {});

  return newPayload;
};

export const fetchRecipientCountries =
  (recipientId: string) =>
  async (
    dispatch: DispatchAction,
    getState: () => RootState,
    { apiV3 }: any,
  ) => {
    const state = getState();
    const isFetchingRecipient = isFetching(state, 'recipient');

    if (isFetchingRecipient) {
      return Promise.resolve();
    }
    dispatch({
      type: FETCH_RECIPIENT_COUNTRIES_REQUEST,
    });

    const response = await apiV3.recipients.fetchCountries({
      recipientId,
      locale: getCurrentLocale(state),
    });

    dispatch({
      type: FETCH_RECIPIENT_COUNTRIES_SUCCESS,
      payload: normalize(response, schemas.arrayOfCountries),
    });
  };

export const resetRecipientHiddenFields =
  () => (dispatch: DispatchAction, getState: () => RootState) => {
    const state = getState();

    const { byId = {}, ids = [] } = getFieldByEntity(state, 'recipient') || {};

    const fields = ids.reduce((acc, current) => {
      if (!get(byId, `${current}.hidden`, false)) {
        return acc;
      }

      return { ...acc, [current]: get(byId, `${current}.value`, false) };
    }, {});

    return dispatch({
      type: SET_FIELDS_VALUES,
      payload: { fields },
    });
  };
